import axios from "axios";
import { loadModules } from "esri-loader";

export default class MapService {
  constructor() {
    // Konstruktør for MapService. Initialiserer kontrollvariabler for spørringer.
    this.countQueryRunning = false;
    this.controller = new AbortController();
    this.signal = null;
  }

  /**
   * Henter unike/distinkte verdier for et felt fra en karttjeneste.
   * @param {string} fieldName - Navn på feltet.
   * @param {string} url - URL til tjenesten (valgfritt).
   * @param {string} outfields - Felter som skal returneres (valgfritt).
   * @param {string} orderByField - Felt for sortering (valgfritt).
   * @param {string} where - Where-spørring (valgfritt).
   * @returns {Promise<Array>} - Liste med unike verdier.
   */
  static getUniqueValues(fieldName, url, outfields, orderByField, where) {
    let mapServiceUrl =
      url ||
      process.env.VUE_APP_BASE_URL +
        "/" +
        process.env.VUE_APP_BASE_URL_LAYER_INDEX;
    let outFields = outfields || fieldName;
    let orderBy = orderByField || fieldName;
    let whereQuery = where || "";

    return axios
      .get(mapServiceUrl + "/query?", {
        params: {
          where: whereQuery
            ? "(" + fieldName + " IS NOT NULL" + ") AND (" + whereQuery + ")"
            : fieldName + " IS NOT NULL",
          outFields: outFields,
          returnGeometry: false,
          returnDistinctValues: true,
          orderByFields: orderBy,
          f: "json",
        },
      })
      .then((uniqueValues) => {
        let uniqueValuesList = [];
        if (outfields) {
          for (let i = 0; i < uniqueValues.data.features.length; i++) {
            uniqueValuesList.push(uniqueValues.data.features[i].attributes);
          }
        } else {
          for (let i = 0; i < uniqueValues.data.features.length; i++) {
            let attributes = uniqueValues.data.features[i].attributes;
            // Case-insensitive attribute lookup to handle field name casing differences between map service versions
            let value = attributes[fieldName];
            if (value === undefined) {
              let fieldNameLower = fieldName.toLowerCase();
              let matchingKey = Object.keys(attributes).find(key => key.toLowerCase() === fieldNameLower);
              if (matchingKey) {
                value = attributes[matchingKey];
              }
            }
            uniqueValuesList.push(value);
          }
        }

        return uniqueValuesList;
      });
  }

  /**
   * Lager HAVING-klausul for kompletthetsspørring basert på alternativer.
   * @param {Object} options - Alternativer for HAVING-klausul.
   * @returns {string} - HAVING-klausul.
   */
  static getHavingClause(options) {
    if (options.useSeasons) {
      let theClause = "";

      options.seasons.forEach((season) => {
        if (season.checked == true) {
          if (theClause !== "") {
            theClause = theClause + " AND ";
          }

          theClause =
            theClause +
            "MIN(" +
            season.name +
            "IkkeManglende) >= " +
            options.maxMissingData +
            " AND MIN(" +
            season.name +
            "Ukorrigerte) >= " +
            options.maxUncorrected;
        }
      });

      return theClause;
    } else {
      return (
        "MIN(aarIkkeManglende) >= " +
        options.maxMissingData +
        " AND MIN(aarUkorrigerte) >= " +
        options.maxUncorrected
      );
    }
  }

  /**
   * Henter tidsserier med kompletthet basert på alternativer.
   * @param {Object} options - Alternativer for spørring.
   * @returns {Promise<string>} - Spørringsstreng for tidsserier.
   */
  static getTimeseriesWithCompleteness(options) {
    const mapServiceUrl = process.env.VUE_APP_DATA_QUALITY_SERVICE_URL;

    const having = this.getHavingClause(options);

    let yearFilterClause =
      options.yearFilterMode.id === "full"
        ? " AND dataFraDato <= DATE '" +
          options.fromYear +
          "-01-01'" +
          " AND (dataTilDato IS NULL OR dataTilDato >= DATE '" +
          options.toYear +
          "-12-31')"
        : " AND (dataFraDato <= DATE '" +
          options.toYear +
          "-12-31'" +
          " AND (dataTilDato IS NULL OR dataTilDato >= DATE '" +
          options.fromYear +
          "-01-01'))";

    return new Promise((resolve, reject) => {
      loadModules(["esri/tasks/QueryTask", "esri/tasks/support/Query"]).then(
        ([QueryTask, Query]) => {
          var query = new Query();
          var queryTask = new QueryTask(mapServiceUrl);
          query.where =
            "komplettAar >= " +
            options.fromYear +
            " AND komplettAar <= " +
            options.toYear +
            yearFilterClause;
          query.outStatistics = [
            {
              statisticType: "avg",
              onStatisticField: "aarIkkeManglende",
              outStatisticFieldName: "aarIkkeManglendeGjsnitt",
            },
          ];
          query.groupByFieldsForStatistics = "compound_key_20";
          query.having = having;

          queryTask
            .execute(query)
            .then((result) => {
              let query = "compoundKey20 IN (";

              if (result.features && result.features.length > 0) {
                result.features.forEach((feature) => {
                  query =
                    query + "'" + feature.attributes.compound_key_20 + "',";
                });
                query = query.substring(0, query.length - 1);
                query = query + ")";
                resolve(query);
              } else {
                resolve("1=2");
              }
            })
            .catch((error) => {
              reject(error);
            });
        }
      );
    });
  }

  /**
   * Henter minimums- og maksimumsverdier for et felt i karttjenesten.
   * @param {string} fieldName - Navn på feltet.
   * @param {Object} options - Alternativer (f.eks. desimaler).
   * @param {string} queryString - Where-spørring (valgfritt).
   * @param {Object} extent - Geometri/utstrekning (valgfritt).
   * @returns {Promise<Object>} - Objekt med min og max.
   */
  static getMinAndMaxValues(fieldName, options, queryString, extent) {
    let whereQuery = queryString || "";

    let mapServiceUrl =
      process.env.VUE_APP_BASE_URL +
      "/" +
      process.env.VUE_APP_BASE_URL_LAYER_INDEX;
    return new Promise((resolve, reject) => {
      loadModules(["esri/tasks/QueryTask", "esri/tasks/support/Query"]).then(
        ([QueryTask, Query]) => {
          var query = new Query();
          var queryTask = new QueryTask(mapServiceUrl);

          query.outStatistics = [
            {
              statisticType: "min",
              onStatisticField: fieldName,
              outStatisticFieldName: "Min" + fieldName,
            },
            {
              statisticType: "max",
              onStatisticField: fieldName,
              outStatisticFieldName: "Max" + fieldName,
            },
          ];

          query.where = whereQuery;
          query.geometry = extent;

          queryTask
            .execute(query)
            .then((minMaxValues) => {
              let attrs = minMaxValues.features[0].attributes;
              let minKey = "Min" + fieldName;
              let maxKey = "Max" + fieldName;
              // Case-insensitive attribute lookup for compatibility with different map service versions
              if (attrs[minKey] === undefined || attrs[maxKey] === undefined) {
                let keys = Object.keys(attrs);
                let minKeyLower = minKey.toLowerCase();
                let maxKeyLower = maxKey.toLowerCase();
                let foundMinKey = keys.find(k => k.toLowerCase() === minKeyLower);
                let foundMaxKey = keys.find(k => k.toLowerCase() === maxKeyLower);
                if (foundMinKey) minKey = foundMinKey;
                if (foundMaxKey) maxKey = foundMaxKey;
              }
              resolve({
                min: options.decimals
                  ? attrs[minKey]
                  : Math.ceil(attrs[minKey]),
                max: options.decimals
                  ? attrs[maxKey]
                  : Math.ceil(attrs[maxKey]),
              });
            })
            .catch((error) => {
              reject(error);
            });
        }
      );
    });
  }

  /**
   * Konverterer en shape-fil til et FeatureCollection-objekt.
   * @param {File} zipFile - Shape-fil (zip).
   * @param {string} name - Navn på datasettet.
   * @returns {Promise} - Axios-respons.
   */
  convertShapeToFeatureCollection(zipFile, name) {
    var form = new FormData();
    var publishParams = {
      name: name,
      targetSR: {
        wkid: "25833",
      },
      maxRecordCount: 1000,
      enforceInputFileSizeLimit: true,
      enforceOutputJsonSizeLimit: true,
    };

    publishParams.generalize = false;
    publishParams.reducePrecision = false;
    publishParams.numberOfDigitsAfterDecimal = 0;
    form.append("publishParameters", JSON.stringify(publishParams));
    form.append("filetype", "shapefile");
    form.append("f", "json");
    let zipName = name + ".zip";
    form.append("file", zipFile, zipName);

    return axios.post(process.env.VUE_APP_IMPORT_SHAPE_SERVICE_URL, form);
  }

  /**
   * Henter vannføringskurvedata for en gitt tidsserie.
   * @param {string} timeseriesID - ID for tidsserien.
   * @returns {Promise<Object>} - Resultater fra spørring.
   */
  static getTimeseriesVannforingsKurve(timeseriesID) {
    let whereQuery = "compound_key_20 = '" + timeseriesID + "'";

    let mapServiceUrl =
      process.env.VUE_APP_BASE_URL +
      "/" +
      process.env.VUE_APP_BASE_URL_TABLE_INDEX;

    return new Promise((resolve, reject) => {
      loadModules(["esri/tasks/QueryTask", "esri/tasks/support/Query"]).then(
        ([QueryTask, Query]) => {
          var query = new Query();
          var queryTask = new QueryTask(mapServiceUrl);
          query.where = whereQuery;
          query.outFields = "*";
          query.orderByFields = ["Versjon"];

          queryTask
            .execute(query)
            .then((results) => {
              resolve(results);
            })
            .catch((error) => {
              reject(error);
            });
        }
      );
    });
  }

  /**
   * Henter antall objekter i karttjenesten som matcher en spørring.
   * @param {string} dataUrl - URL til tjenesten.
   * @param {string} queryString - Where-spørring.
   * @param {Object} mapExtent - Kartutstrekning.
   * @param {boolean} useBuffer - Om buffer skal brukes.
   * @param {Object} buffer - Buffer-objekt.
   * @param {boolean} useMapExtent - Om kartutstrekning skal brukes.
   * @returns {Promise<number>} - Antall treff.
   */
  async getCount(
    dataUrl,
    queryString,
    mapExtent,
    useBuffer,
    buffer,
    useMapExtent
  ) {
    return loadModules([
      "esri/tasks/QueryTask",
      "esri/tasks/support/Query",
    ]).then(async ([QueryTask, Query]) => {
      if (this.countQueryRunning) {
        this.controller.abort();
      }

      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.countQueryRunning = true;

      var query = new Query();
      var queryTask = new QueryTask(dataUrl);

      if (useBuffer) {
        query.geometry = buffer.point;
        query.distance = buffer.distance;
        query.units = "kilometers";
      } else {
        let extent = await this.getQueryGeometry(mapExtent, useMapExtent);
        query.geometry = extent;
      }
      query.where = queryString;

      return queryTask.executeForCount(query, {
        signal: this.signal,
      });
    });
  }

  /**
   * Returnerer geometri for spørring basert på polygon eller utstrekning.
   * @param {Object} geometry - Geometriobjekt.
   * @param {boolean} useMapExtent - Om kartutstrekning skal brukes.
   * @returns {Promise<Object>} - Geometri for spørring.
   */
  async getQueryGeometry(geometry, useMapExtent) {
    return loadModules(["esri/geometry/Polygon", "esri/geometry/Extent"]).then(
      ([Polygon, Extent]) => {
        let extent = null;
        if (Object.prototype.hasOwnProperty.call(geometry, "rings")) {
          extent = new Polygon(geometry);
          extent.type = "polygon";
        } else {
          if (useMapExtent) {
            extent = new Extent(geometry);
            extent.type = "extent";
          } else {
            extent = null;
          }
        }

        return extent;
      }
    );
  }

  /**
   * Henter et spesifisert antall dataobjekter/tidsserier fra karttjeneste basert på spørring og filter.
   * @param {string} dataUrl - URL til tjenesten.
   * @param {string} queryString - Where-spørring.
   * @param {Object} currentMapExtent - Kartutstrekning.
   * @param {boolean} useBuffer - Om buffer skal brukes.
   * @param {Object} buffer - Buffer-objekt.
   * @param {boolean} useMapExtent - Om kartutstrekning skal brukes.
   * @param {Object} defaultMapExtent - Standard kartutstrekning.
   * @param {Object} options - Alternativer for sortering og paginering.
   * @param {number} page - Side for paginering.
   * @returns {Promise<Object>} - Objekt med antall, data og spørring.
   */
  async getData(
    dataUrl,
    queryString,
    currentMapExtent,
    useBuffer,
    buffer,
    useMapExtent,
    defaultMapExtent,
    options,
    page
  ) {
    this.loading = true;

    return loadModules([
      "esri/tasks/QueryTask",
      "esri/tasks/support/Query",
    ]).then(async ([QueryTask, Query]) => {
      var query = new Query();
      var queryTask = new QueryTask(dataUrl);
      if (useBuffer) {
        query.geometry = buffer.point;
        query.distance = buffer.distance;
        query.units = "kilometers";
      } else {
        query.geometry = await this.getQueryGeometry(
          currentMapExtent,
          useMapExtent
        );
      }

      if (options.sortBy.length > 0 && options.sortDesc.length > 0) {
        query.orderByFields = [];
        for (let i = 0; i < options.sortBy.length; i++) {
          query.orderByFields.push(
            options.sortBy[i] + " " + (options.sortDesc[i] ? "DESC" : "ASC")
          );
        }
      }
      query.outFields = "*";

      query.start = (page - 1) * options.itemsPerPage;

      query.num = options.itemsPerPage;
      query.returnGeometry = true;

      query.where = queryString;

      return new Promise(
        function(resolve, reject) {
          this.getCount(
            dataUrl,
            queryString,
            currentMapExtent,
            useBuffer,
            buffer,
            useMapExtent
          )
            .then((result) => {
              this.countQueryRunning = false;

              return queryTask.execute(query).then((resp) => {
                // Normaliser feltnavnene fra lowercase til camelCase for bakoverkompatibilitet
                const fieldMapping = {
                  'objectid': 'OBJECTID',
                  'objekttype': 'objektType',
                  'compoundkey12': 'compoundKey12',
                  'compoundkey20': 'compoundKey20',
                  'stasjonnr': 'stasjonNr',
                  'stasjonnavn': 'stasjonNavn',
                  'stasjonparameter': 'stasjonParameter',
                  'stasjontype': 'stasjonType',
                  'stasjonhoyde_moh': 'stasjonHoyde_moh',
                  'stasjonstatus': 'stasjonStatus',
                  'stasjoneier': 'stasjonEier',
                  'startdato': 'startDato',
                  'sluttdato': 'sluttDato',
                  'datafradato': 'dataFraDato',
                  'datatildato': 'dataTilDato',
                  'findatafradato': 'findataFraDato',
                  'vassdragsnr': 'vassdragsNr',
                  'vassdragsomradenr': 'vassdragsOmradeNr',
                  'vassdragsomrnavn': 'vassdragsOmrNavn',
                  'elvenavnhierarki': 'elvenavnHierarki',
                  'observasjonssted': 'observasjonsSted',
                  'feltnr': 'feltNr',
                  'kdbnr': 'kdbNr',
                  'minstevannforing': 'minsteVannforing',
                  'hhomr': 'HHOmr',
                  'reguleringstype': 'reguleringsType',
                  'forsteaarutbygd': 'forsteAarUtbygd',
                  'globalid': 'GlobalID',
                  'normal_arsavrenning_91_20': 'normalAvrenningLsKm',
                  'hoyestepunkt': 'hoyestepunkt',
                  'versjon': 'versjon',
                  'status': 'status',
                  'sanntid': 'sanntid',
                  'gruppe': 'gruppe'
                };

                // Normaliser attributter for alle features
                if (resp.features) {
                  resp.features.forEach(feature => {
                    const normalizedAttrs = {};
                    for (const key in feature.attributes) {
                      const mappedKey = fieldMapping[key.toLowerCase()] || key;
                      normalizedAttrs[mappedKey] = feature.attributes[key];
                    }
                    feature.attributes = normalizedAttrs;
                  });
                }

                resolve({
                  count: result,
                  data: resp,
                  query: query,
                  queryTask: queryTask,
                });
              });
            })
            .catch((error) => {
              reject(error);
            });
        }.bind(this)
      );
    });
  }

  /**
   * Oppdaterer min/maks-filter basert på spørring og utstrekning.
   * @param {Object} filter - Filterobjekt.
   * @param {string} whereQuery - Where-spørring.
   * @param {Object} extent - Geometri/utstrekning.
   * @returns {Promise<Object>} - Oppdaterte min/maks-verdier.
   */
  static updateMinMaxFilter(filter, whereQuery, extent) {
    return new Promise((resolve) => {
      this.getMinAndMaxValues(
        filter.startParameter,
        filter.options,
        whereQuery,
        extent
      ).then((minMaxValues) => {
        if (filter.percent) {
          if (minMaxValues.max > 100) {
            minMaxValues.max = 100;
          }
        }
        if (minMaxValues.min < 0) {
          minMaxValues.min = 0;
        }
        resolve(minMaxValues);
      });
    });
  }

  /**
   * Henter de mest relevante alternativene for et filter.
   * @param {Object} filter - Filterobjekt.
   * @param {Object} filterCategory - Filterkategori.
   * @param {Array} uniqueValues - Unike verdier.
   * @returns {Object} - Objekt med mest relevante alternativer.
   */
  static getMostRelevantOptions(filter, filterCategory, uniqueValues) {
    let mostRelevantOptions = {
      group: "Mest brukte",
      options: [],
    };
    if (filter.name === "observasjonsSted") {
      if (
        Object.prototype.hasOwnProperty.call(
          filterCategory.filters[0],
          "currentValue"
        ) &&
        filterCategory.filters[0].currentValue.length > 0
      ) {
        let stasjonParameterValues = filterCategory.filters[0].currentValue;

        for (let i = 0; i < stasjonParameterValues.length; i++) {
          let stasjonParameterValue = stasjonParameterValues[i];
          if (
            Object.prototype.hasOwnProperty.call(
              filter.mostRelevantOptionsSpecificForParameters,
              stasjonParameterValue
            )
          ) {
            filter.mostRelevantOptionsSpecificForParameters[
              stasjonParameterValue
            ].forEach((specificRelevantOption) => {
              if (
                !mostRelevantOptions.options.includes(specificRelevantOption)
              ) {
                mostRelevantOptions.options.push(specificRelevantOption);
              }
            });
          }
        }
      } else {
        mostRelevantOptions.options = filter.mostRelevantOptions.filter(
          (value) => {
            return uniqueValues.includes(value);
          }
        );
      }
    } else {
      mostRelevantOptions.options = filter.mostRelevantOptions.filter(
        (value) => {
          return uniqueValues.includes(value);
        }
      );
    }
    return mostRelevantOptions;
  }

  /**
   * Henter mindre relevante alternativer for et filter.
   * @param {Array} uniqueValues - Unike verdier.
   * @param {Object} mostRelevantOptions - Mest relevante alternativer.
   * @returns {Object} - Objekt med mindre relevante alternativer.
   */
  static getLessRelevantOptions(uniqueValues, mostRelevantOptions) {
    let lessRelevantOptions = {
      group: "Andre",
      options: [],
    };
    uniqueValues.forEach((value) => {
      if (!mostRelevantOptions.options.includes(value)) {
        lessRelevantOptions.options.push(value);
      }
    });
    return lessRelevantOptions;
  }

  /**
   * Oppdaterer unike filterverdier basert på spørring og kategori.
   * @param {Object} filter - Filterobjekt.
   * @param {string} whereQuery - Where-spørring.
   * @param {Object} filterCategory - Filterkategori.
   * @returns {Promise<Array|Object>} - Oppdaterte filterverdier.
   */
  static updateUniqueFilter(filter, whereQuery, filterCategory) {
    return new Promise((resolve) => {
      if (filter.fetchUrl && filter.fetchParameter) {
        this.getUniqueValues(
          filter.fetchParameter,
          filter.fetchUrl,
          filter.fetchOutfields,
          filter.orderByField,
          whereQuery
        )
          .then((uniqueValues) => {
            filter.values = uniqueValues;
            resolve(uniqueValues);
          })
          .catch((error) => {
            console.error(error);
          });
      } else {
        this.getUniqueValues(filter.name, null, null, null, whereQuery)
          .then((uniqueValues) => {
            if (filter.group) {
              let mostRelevantOptions = this.getMostRelevantOptions(
                filter,
                filterCategory,
                uniqueValues
              );
              let lessRelevantOptions = this.getLessRelevantOptions(
                uniqueValues,
                mostRelevantOptions
              );

              resolve([mostRelevantOptions, lessRelevantOptions]);
            } else {
              resolve(uniqueValues);
            }
          })
          .catch((error) => {
            console.error(error);
          });
      }
    });
  }

  /**
   * Laster inn bakgrunnskart basert på definerte basemaps.
   * @param {Array} basemapDefinitions - Definisjoner av bakgrunnskart.
   * @returns {Promise<Array>} - Liste med innlastede bakgrunnskart.
   */
  static loadBasemaps(basemapDefinitions) {
    return new Promise((resolve, reject) => {
      loadModules([
        "esri/layers/VectorTileLayer",
        "esri/layers/TileLayer",
        "esri/Basemap",
      ])
        .then(([VectorTileLayer, TileLayer, Basemap]) => {
          let loadedBasemaps = [];
          basemapDefinitions.forEach((basemap) => {
            let mapBaseLayer;
            if (basemap.isVector) {
              mapBaseLayer = new VectorTileLayer({
                url: basemap.urlTemplate,
              });
            } else {
              mapBaseLayer = new TileLayer({
                url: basemap.urlTemplate,
              });
            }
            let arcgisBasemap = new Basemap({
              baseLayers: [mapBaseLayer],
              title: basemap.title,
              id: basemap.id,
              thumbnailUrl: basemap.thumbnailUrl,
            });
            loadedBasemaps.push(arcgisBasemap);
          });
          resolve(loadedBasemaps);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
